import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

const AssignSubject = () => {
    // State Management
    // Centralized base path for Course-Subject assignment API (admin-prefixed)
    const COURSE_SUBJECTS_BASE = '/admin/course-subjects';
    const [courses, setCourses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [subjectSearchQuery, setSubjectSearchQuery] = useState('');
    const [viewSubjectSearchQuery, setViewSubjectSearchQuery] = useState('');
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [assignedSubjects, setAssignedSubjects] = useState([]);
    const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initial load of courses and subjects
    const fetchInitialData = async () => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(COURSE_SUBJECTS_BASE);
            const data = res.data || {};
            const courseList = Array.isArray(data.courses) ? data.courses : [];
            const subjectList = Array.isArray(data.subjects) ? data.subjects : [];
            setCourses(courseList);
            setSubjects(subjectList);
        } catch (error) {
            console.error('Error loading course-subject data:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: 'Failed to load courses and subjects.',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filter courses based on search
    const filteredCourses = courses.filter((course) =>
        course.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.shortName.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Filter subjects based on search in assign modal
    const filteredSubjects = subjects.filter((subject) =>
        subject.fullName.toLowerCase().includes(subjectSearchQuery.toLowerCase()) ||
        subject.shortName.toLowerCase().includes(subjectSearchQuery.toLowerCase())
    );

    // Filter assigned subjects based on search in view modal
    const filteredAssignedSubjects = assignedSubjects.filter((subject) =>
        subject.fullName.toLowerCase().includes(viewSubjectSearchQuery.toLowerCase()) ||
        subject.shortName.toLowerCase().includes(viewSubjectSearchQuery.toLowerCase())
    );

    // Fetch assigned subjects for a course
    const fetchAssignedSubjects = async (course) => {
        setIsLoading(true);
        try {
            const res = await axiosInstance.get(`${COURSE_SUBJECTS_BASE}/${course.courseId}/subjects`);
            const data = res.data;

            if (data.success) {
                setAssignedSubjects(data.subjects);
            } else {
                throw new Error(data.message);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.message || 'Failed to fetch assigned subjects',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle View Subjects button click
    const handleViewSubjects = async (course) => {
        setSelectedCourse(course);
        await fetchAssignedSubjects(course);
        setIsViewModalOpen(true);
    };

    // Handle Assign button click
    const handleAssignClick = async (course) => {
        setSelectedCourse(course);
        setIsLoading(true);
        
        try {
            const res = await axiosInstance.get(`${COURSE_SUBJECTS_BASE}/${course.courseId}/subjects`);
            const data = res.data;

            if (data.success) {
                // Set currently assigned subject IDs as selected
                const assignedIds = data.subjects.map((s) => s.subjectId);
                setSelectedSubjectIds(assignedIds);
                setIsAssignModalOpen(true);
            } else {
                throw new Error(data.message);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: error.message || 'Failed to load subjects',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle checkbox toggle
    const handleCheckboxChange = (subjectId) => {
        setSelectedSubjectIds((prev) =>
            prev.includes(subjectId)
                ? prev.filter((id) => id !== subjectId)
                : [...prev, subjectId]
        );
    };

    // Handle assign subjects submission
    const handleAssignSubmit = async () => {
        if (!selectedCourse) return;

        setIsSubmitting(true);

        try {
            const cleanedSubjectIds = Array.isArray(selectedSubjectIds)
                ? selectedSubjectIds
                    .map((id) => Number(id))
                    .filter((id) => Number.isInteger(id) && id > 0)
                : [];

            const res = await axiosInstance.post(`${COURSE_SUBJECTS_BASE}/${selectedCourse.courseId}/assign`, {
                subjectIds: cleanedSubjectIds,
            });

            const data = res.data;

            if (data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: data.message,
                    confirmButtonColor: '#4f46e5',
                });
                setIsAssignModalOpen(false);
                setSelectedSubjectIds([]);
            } else {
                throw new Error(data.message);
            }
        } catch (error) {
            const apiMessage = error.response?.data?.message;
            const apiErrors = error.response?.data?.errors;
            const details = Array.isArray(apiErrors) && apiErrors.length
                ? apiErrors.join('\n')
                : null;

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: details || apiMessage || error.message || 'Failed to assign subjects',
                confirmButtonColor: '#ef4444',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle remove subject
    const handleRemoveSubject = async (subjectId) => {
        if (!selectedCourse) return;

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to remove this subject from the course?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, remove it!',
        });

        if (result.isConfirmed) {
            try {
                const res = await axiosInstance.delete(`${COURSE_SUBJECTS_BASE}/${selectedCourse.courseId}/subjects/${subjectId}`);

                const data = res.data;

                if (data.success) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Removed!',
                        text: data.message,
                        confirmButtonColor: '#4f46e5',
                    });
                    // Refresh the assigned subjects list
                    await fetchAssignedSubjects(selectedCourse);
                } else {
                    throw new Error(data.message);
                }
            } catch (error) {
                const apiMessage = error.response?.data?.message;
                const detailsObj = error.response?.data?.details;
                let detailsText = null;
                if (detailsObj && typeof detailsObj === 'object') {
                    const parts = Object.entries(detailsObj)
                        .map(([key, value]) => `${key}: ${value}`);
                    if (parts.length) {
                        detailsText = parts.join('\n');
                    }
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: detailsText || apiMessage || error.message || 'Failed to remove subject',
                    confirmButtonColor: '#ef4444',
                });
            }
        }
    };

    // Close modals
    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedCourse(null);
        setAssignedSubjects([]);
        setViewSubjectSearchQuery(''); // Reset search when closing modal
    };

    const closeAssignModal = () => {
        setIsAssignModalOpen(false);
        setSelectedCourse(null);
        setSelectedSubjectIds([]);
        setSubjectSearchQuery(''); // Reset search when closing modal
    };

    return (
        <DashboardLayout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-800">Assign Subject to Courses</h1>
                    <p className="text-gray-600 mt-1">Manage subject assignments for each course</p>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search courses..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                        </svg>
                    </div>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Total Courses</div>
                        <div className="text-3xl font-bold text-indigo-600">{courses.length}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Total Subjects</div>
                        <div className="text-3xl font-bold text-green-600">{subjects.length}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm text-gray-600">Filtered Courses</div>
                        <div className="text-3xl font-bold text-blue-600">{filteredCourses.length}</div>
                    </div>
                </div>

                {/* Courses Table */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    #
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Course Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Short Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Assigned Subjects
                                </th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredCourses.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                                        No courses found
                                    </td>
                                </tr>
                            ) : (
                                filteredCourses.map((course, index) => (
                                    <tr key={course.courseId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {index + 1}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900">
                                            {course.fullName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {course.shortName}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {course.assignedSubjectCount ?? 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                            <button
                                                onClick={() => handleViewSubjects(course)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                            >
                                                View Subjects
                                            </button>
                                            <button
                                                onClick={() => handleAssignClick(course)}
                                                className="text-indigo-600 hover:text-indigo-900"
                                            >
                                                Assign
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* View Subjects Modal */}
                {isViewModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800">
                                    Assigned Subjects - {selectedCourse?.fullName}
                                </h2>
                            </div>
                            <div className="p-6">
                                {isLoading ? (
                                    <div className="text-center py-8">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-indigo-600"></div>
                                        <p className="mt-2 text-gray-600">Loading...</p>
                                    </div>
                                ) : assignedSubjects.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No subjects assigned to this course yet
                                    </div>
                                ) : (
                                    <>
                                        {/* Search Input for Assigned Subjects */}
                                        <div className="mb-4">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Search assigned subjects..."
                                                    value={viewSubjectSearchQuery}
                                                    onChange={(e) => setViewSubjectSearchQuery(e.target.value)}
                                                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                                />
                                                <svg
                                                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                    />
                                                </svg>
                                            </div>
                                            {viewSubjectSearchQuery && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Found {filteredAssignedSubjects.length} subject(s)
                                                </p>
                                            )}
                                        </div>

                                        {filteredAssignedSubjects.length === 0 ? (
                                            <div className="text-center py-8 text-gray-500">
                                                No subjects found matching "{viewSubjectSearchQuery}"
                                            </div>
                                        ) : (
                                            <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            #
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Subject Name
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Short Name
                                                        </th>
                                                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredAssignedSubjects.map((subject, index) => (
                                                        <tr key={subject.subjectId} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {index + 1}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-900">
                                                                {subject.fullName}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {subject.shortName}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                                                <button
                                                                    onClick={() => handleRemoveSubject(subject.subjectId)}
                                                                    className="text-red-600 hover:text-red-900"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-end">
                                <button
                                    onClick={closeViewModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Assign Subjects Modal */}
                {isAssignModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <h2 className="text-xl font-bold text-gray-800">
                                    Assign Subjects - {selectedCourse?.fullName}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    Select subjects to assign. Uncheck to remove.
                                </p>
                            </div>
                            <div className="p-6">
                                {isLoading ? (
                                    <div className="text-center py-8">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-indigo-600"></div>
                                        <p className="mt-2 text-gray-600">Loading...</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Search Input for Subjects */}
                                        <div className="mb-4">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Search subjects..."
                                                    value={subjectSearchQuery}
                                                    onChange={(e) => setSubjectSearchQuery(e.target.value)}
                                                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                                />
                                                <svg
                                                    className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                                    />
                                                </svg>
                                            </div>
                                            {subjectSearchQuery && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Found {filteredSubjects.length} subject(s)
                                                </p>
                                            )}
                                        </div>

                                        {/* Subjects List */}
                                        <div className="space-y-2 max-h-96 overflow-y-auto">
                                            {filteredSubjects.length === 0 ? (
                                                <div className="text-center py-8 text-gray-500">
                                                    No subjects found matching "{subjectSearchQuery}"
                                                </div>
                                            ) : (
                                                filteredSubjects.map((subject) => (
                                                    <label
                                                        key={subject.subjectId}
                                                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedSubjectIds.includes(subject.subjectId)}
                                                            onChange={() => handleCheckboxChange(subject.subjectId)}
                                                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                                        />
                                                        <div className="ml-3 flex-1">
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {subject.fullName}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {subject.shortName} • {subject.numberOfClasses} classes
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="p-6 border-t border-gray-200 flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                    {selectedSubjectIds.length} subject(s) selected
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={closeAssignModal}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAssignSubmit}
                                        disabled={isSubmitting}
                                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Assigning...' : 'Assign'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default AssignSubject;
